import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Loader2, CheckCheck, AlertCircle, RefreshCw } from 'lucide-react';
import { NotificationService } from '@/services/shared/notificationService';
import type { NotificationDocument } from '@/services/shared/notificationService';
import { NotificationItem } from './NotificationItem';
import { ActivityFeedTab } from './ActivityFeedTab';
import { useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { Z_INDEX } from '@/lib/z-index';

type TabId = 'notifications' | 'activity';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: (notification: NotificationDocument) => void;
  className?: string;
}

/**
 * NotificationDropdown component - Tabbed panel with Notifications and Activity Feed tabs.
 *
 * Features:
 * - Smooth slide-in/slide-out animations
 * - Enhanced loading states and error handling
 * - Keyboard navigation support
 * - Optimistic updates for better responsiveness
 * - Improved accessibility
 * - Auto-refresh functionality
 * - Activity feed tab with compact event display
 */
export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  onNavigate,
  className = ''
}) => {
  const { effectiveUser } = useAuthUnified();
  const [activeTab, setActiveTab] = useState<TabId>('notifications');
  const [notifications, setNotifications] = useState<NotificationDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [_isAnimatingIn, setIsAnimatingIn] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Reset to notifications tab when opened
  useEffect(() => {
    if (isOpen) {
      setActiveTab('notifications');
      setIsAnimatingIn(false);
    }
  }, [isOpen]);

  // Subscribe to real-time notifications with enhanced error handling
  useEffect(() => {
    if (!isOpen || !effectiveUser?.employee_id) return;

    setLoading(true);
    setError(null);

    const unsubscribe = NotificationService.subscribeToUserNotifications(
      effectiveUser.employee_id.toString(),
      (updatedNotifications) => {
        setNotifications(updatedNotifications);
        setLoading(false);
        setRefreshing(false);
        setError(null);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isOpen, effectiveUser?.employee_id]);

  // Handle click outside to close dropdown with improved event handling
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      // Delay adding the listener to avoid catching the same click that opened the dropdown
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, onClose]);

  // Memoized handlers for better performance
  const handleMarkAsRead = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, status: 'read' as const } : n)
      );

      await NotificationService.markAsRead(notificationId);
    } catch (err) {
      setError('Failed to mark notification as read');
    }
  }, []);

  const handleDismiss = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      await NotificationService.dismissNotification(notificationId);
    } catch (err) {
      setError('Failed to dismiss notification');
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    if (!effectiveUser?.employee_id || markingAllAsRead) return;

    try {
      setMarkingAllAsRead(true);
      setError(null);

      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' as const })));

      await NotificationService.markAllAsRead(effectiveUser.employee_id.toString());
    } catch (err) {
      setError('Failed to mark all notifications as read');
    } finally {
      setMarkingAllAsRead(false);
    }
  }, [effectiveUser?.employee_id, markingAllAsRead]);

  const handleRefresh = useCallback(async () => {
    if (!effectiveUser?.employee_id || refreshing) return;

    try {
      setRefreshing(true);
      setError(null);

      const freshNotifications = await NotificationService.getUserNotifications(
        effectiveUser.employee_id.toString()
      );
      setNotifications(freshNotifications);
    } catch (err) {
      setError('Failed to refresh notifications');
    } finally {
      setRefreshing(false);
    }
  }, [effectiveUser?.employee_id, refreshing]);

  // Memoized computed values
  const unreadCount = useMemo(() =>
    notifications.filter(n => n.status === 'unread').length,
    [notifications]
  );

  const hasNotifications = notifications.length > 0;

  // Limit displayed notifications to 5
  const MAX_DISPLAYED_NOTIFICATIONS = 5;
  const displayedNotifications = useMemo(() =>
    notifications.slice(0, MAX_DISPLAYED_NOTIFICATIONS),
    [notifications]
  );

  const hasMoreNotifications = notifications.length > MAX_DISPLAYED_NOTIFICATIONS;

  if (!isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      className={`
        absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-xl ring-1 ring-black ring-opacity-5 flex flex-col
        transform transition-all duration-200 ease-out origin-top-right
        animate-in slide-in-from-top-2 fade-in-0
        ${className}
      `}
      style={{ zIndex: Z_INDEX.FLOATING_DROPDOWNS }}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-label="Notifications and Activity"
      aria-modal="false"
    >
      {/* Header */}
      <div className="px-4 pt-3 pb-0 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-lg">
        <div className="flex items-center justify-between mb-2">
          {/* Tab bar */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'notifications'
                  ? 'border-purple-600 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Notifications
              {unreadCount > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                  {unreadCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('activity')}
              className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'activity'
                  ? 'border-purple-600 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Activity
            </button>
          </div>

          {/* Actions — only on Notifications tab */}
          {activeTab === 'notifications' && (
            <div className="flex items-center gap-2 pb-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200 disabled:opacity-50"
                aria-label="Refresh notifications"
                title="Refresh notifications"
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>

              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={markingAllAsRead}
                  className="text-xs text-purple-600 hover:text-purple-800 disabled:opacity-50 flex items-center gap-1 px-2 py-1 rounded-md hover:bg-purple-50 transition-colors duration-200"
                  aria-label="Mark all notifications as read"
                >
                  {markingAllAsRead ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <CheckCheck className="h-3 w-3" />
                  )}
                  <span>Mark all read</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'notifications' ? (
        <>
          {/* Notifications content */}
          <div className="flex-1">
            {loading && notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-purple-500 mb-3" />
                <span className="text-sm text-gray-500">Loading notifications...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <AlertCircle className="h-8 w-8 text-red-400 mb-3" />
                <span className="text-sm text-red-600 text-center">{error}</span>
                <button
                  onClick={handleRefresh}
                  className="mt-3 text-xs text-purple-600 hover:text-purple-800 underline"
                >
                  Try again
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="h-16 w-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mb-4">
                  <CheckCheck className="h-8 w-8 text-purple-500" />
                </div>
                <p className="text-sm font-medium text-gray-900 text-center mb-1">
                  All caught up!
                </p>
                <p className="text-xs text-gray-500 text-center">
                  No new notifications to show
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {displayedNotifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={handleMarkAsRead}
                    onDismiss={handleDismiss}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Notifications footer */}
          {hasNotifications && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <p className="text-xs text-gray-500 text-center">
                {hasMoreNotifications
                  ? `Showing ${displayedNotifications.length}/${notifications.length} notifications`
                  : `Showing all ${notifications.length} notification${notifications.length === 1 ? '' : 's'}`
                }
              </p>
            </div>
          )}
        </>
      ) : (
        <ActivityFeedTab />
      )}
    </div>
  );
};
